import Pagination from "@mui/material/Pagination";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import useNotifications, { NotificationsSortingType } from "../../hooks/useNotifications";
import useNotificationsWithFocusArea from "../../hooks/useNotificationsWithFocusArea";
import usePagination from "../../hooks/usePagination";
import { useLeadStore, usePipedriveStore } from "../../index";
import { NotificationType } from "../../interfaces/INotification";
import { formatNumber } from "../../utils/utils";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import DealNotifictionInfo from "./DealNotifictionInfo";
import LeadNotificationInfo from "./LeadNotificationInfo";

function Notifications() {
    const pipedriveStore = usePipedriveStore();
    const leadStore = useLeadStore();
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(NotificationsSortingType.DueDateNewest);
    const [page, setPage] = useState(1);
    const notifications = useNotificationsWithFocusArea(useNotifications(debouncedSearchFilter, sortBy));
    const [pageCount, pagedNotifications] = usePagination(notifications, page, () => setPage(1));
    const isLoading = useMemo(() => computed(() => pipedriveStore.isDealsLoading.fetching || leadStore.isLeadsLoading.fetching), []).get();

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>{!isLoading && formatNumber(notifications.length)} Notifications</div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus placeholder="Search notifications.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={NotificationsSortingType.ModifiedNewest}>Modified (Newest)</option>
                        <option value={NotificationsSortingType.ModifiedOldest}>Modified (Oldest)</option>
                        <option value={NotificationsSortingType.CreatedNewest}>Created (Newest)</option>
                        <option value={NotificationsSortingType.CreatedOldest}>Created (Oldest)</option>
                        <option value={NotificationsSortingType.DueDateNewest}>DueDate (Newest)</option>
                        <option value={NotificationsSortingType.DueDateOldest}>DueDate (Oldest)</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {isLoading ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedNotifications.map(n => n.type === NotificationType.Lead
                        ? (
                            <LeadNotificationInfo
                                key={`lead-${n.leadId}`}
                                className={tabStyles.item}
                                leadId={n.leadId!}
                                name={n.name}
                                dueDate={n.dueDate!}
                                subject={n.subject!}
                            />
                        ) : (
                            <DealNotifictionInfo
                                key={`deal-${n.dealId}`}
                                className={tabStyles.item}
                                dealId={n.dealId!}
                                name={n.name}
                                address={n.address!}
                            />
                        )
                    )
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    );
}

export default observer(Notifications);